import React, { useCallback, useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { adaptV4Theme, createTheme, ThemeProvider } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@material-ui/core";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { api } from "./api/client";
import MalzemeListesiOnizleme from "./MalzemeListesiOnizleme";
import ModalboxEkler from "./ModalboxEkler";
import "./Pano.css";
import { enhanceColumns, withTableOptions } from "./utils/tableDisplay";

const BRAND = "#ff5100";

const theme = createTheme(
  adaptV4Theme({
    palette: {
      primary: { main: BRAND },
      warning: { main: "#ffa426" },
      error: { main: "#fc544b" },
    },
    overrides: {
      MUIDataTable: {
        responsiveBase: { overflow: "auto" },
        paper: { boxShadow: "none" },
      },
      MUIDataTableHeadCell: {
        toolButton: {
          justifyContent: "flex-start",
          fontSize: "0.68rem",
          padding: "2px 0",
          fontWeight: 650,
          color: "#6c757d",
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        },
        root: {
          padding: "6px 8px",
          fontSize: "0.68rem",
          backgroundColor: "#fafbfc !important",
          borderBottom: "1px solid #e3eaef !important",
        },
      },
      MUIDataTableBodyCell: {
        root: {
          padding: "5px 8px",
          fontSize: "0.78rem",
          borderBottom: "1px solid #f0f3f5 !important",
          color: "#191d21",
        },
      },
      MUIDataTableToolbar: {
        root: { minHeight: 42, paddingLeft: 8, paddingRight: 8 },
        titleText: { display: "none" },
        icon: { color: "#98a6ad" },
      },
      MUIDataTableSearch: {
        searchIcon: { color: "#98a6ad" },
      },
      MUIDataTableBodyRow: {
        root: {
          "&:hover": { backgroundColor: "rgba(255, 81, 0, 0.04) !important" },
          "&:nth-of-type(odd)": { backgroundColor: "#fcfcfd" },
        },
      },
      MuiCheckbox: { root: { padding: 2, color: "#cdd3d8" }, colorPrimary: { "&.Mui-checked": { color: BRAND } } },
      MuiTablePagination: {
        root: { fontSize: "0.72rem", color: "#6c757d", borderTop: "1px solid #e3eaef" },
        toolbar: { minHeight: 36 },
        selectIcon: { color: "#98a6ad" },
      },
      MuiPaper: { elevation4: { boxShadow: "none" } },
    },
  })
);

function fmtDate(value) {
  if (!value) return "—";
  return moment(value).format("DD/MM/YY");
}

function DateChip({ value, empty = "—" }) {
  if (!value) return <span className="pano__date">{empty}</span>;
  return <span className="pano__date">{fmtDate(value)}</span>;
}

export default function Pano() {
  const [teslim, setTeslim] = useState([]);
  const [gecerlilik, setGecerlilik] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iptalOpen, setIptalOpen] = useState(false);
  const [iptalTeklifNo, setIptalTeklifNo] = useState("");
  const [redNotlari, setRedNotlari] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/teklifler/pano");
      setTeslim(data.teslim_yaklasan || []);
      setGecerlilik(data.gecerlilik_yaklasan || []);
    } catch (err) {
      console.error(err);
      toast.error("Pano verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleOtoMail = async (teklifNo, checked) => {
    const text = checked
      ? "Bu teklif için hatırlatma postaları otomatik gidecek. Onaylıyor musunuz?"
      : "Bu teklif için hatırlatma epostaları otomatik gitmeyecek. Onaylıyor musunuz?";
    if (!window.confirm(text)) {
      load();
      return;
    }
    try {
      await api.patch(`/teklifler/${encodeURIComponent(teklifNo)}`, {
        otomatik_eposta: checked,
      });
      toast.success(checked ? "Otomatik hatırlatma açıldı" : "Otomatik hatırlatma kapatıldı");
      setGecerlilik((rows) =>
        rows.map((r) =>
          r.teklif_no === teklifNo ? { ...r, otomatik_eposta: checked } : r
        )
      );
    } catch (err) {
      console.error(err);
      toast.error("Güncelleme başarısız");
      load();
    }
  };

  const toggleOtoIptal = async (teklifNo, checked) => {
    const text = checked
      ? "Bu teklif otomatik iptal olacak. Onaylıyor musunuz?"
      : "Bu teklif otomatik iptal olmayacak. Onaylıyor musunuz?";
    if (!window.confirm(text)) {
      load();
      return;
    }
    try {
      await api.patch(`/teklifler/${encodeURIComponent(teklifNo)}`, {
        otomatik_iptal: checked ? "1" : "0",
      });
      toast.success(checked ? "Otomatik iptal açıldı" : "Otomatik iptal kapatıldı");
      setGecerlilik((rows) =>
        rows.map((r) =>
          r.teklif_no === teklifNo ? { ...r, otomatik_iptal: checked } : r
        )
      );
    } catch (err) {
      console.error(err);
      toast.error("Güncelleme başarısız");
      load();
    }
  };

  const openIptal = (teklifNo) => {
    setIptalTeklifNo(teklifNo);
    setRedNotlari("");
    setIptalOpen(true);
  };

  const submitIptal = async () => {
    try {
      await api.post(`/teklifler/${encodeURIComponent(iptalTeklifNo)}/iptal`, {
        red_notlari: redNotlari || "",
        mail_at: true,
      });
      toast.success("İptal maili gönderildi / teklif iptal edildi");
      setIptalOpen(false);
      load();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "İptal başarısız");
    }
  };

  const hatirlat = async (teklifNo) => {
    try {
      await api.post(`/teklifler/${encodeURIComponent(teklifNo)}/hatirlat`, {});
      toast.success("Hatırlatma kuyruğa alındı");
      load();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Hatırlatma başarısız");
    }
  };

  const commonOptions = {
    filter: false,
    responsive: "vertical",
    selectableRows: "none",
    download: false,
    print: false,
    viewColumns: false,
    search: true,
    elevation: 0,
    rowsPerPage: 12,
    rowsPerPageOptions: [12, 25, 50],
    setTableProps: () => ({ size: "small" }),
    textLabels: {
      body: { noMatch: loading ? "Yükleniyor…" : "Kayıt yok" },
      toolbar: { search: "Ara" },
      pagination: {
        next: "Sonraki",
        previous: "Önceki",
        rowsPerPage: "Satır:",
        displayRows: "/",
      },
    },
  };

  const teslimColumns = [
    {
      name: "teklif_no",
      label: "Teklif No",
      options: {
        filter: false,
        customBodyRender: (value, meta) => {
          const record = teslim[meta.currentTableData[meta.rowIndex].index];
          if (!value || !record) return value;
          return (
            <MalzemeListesiOnizleme
              teklifNo={value}
              teklifDili={record.teklif_dili}
              teklifTarihi={record.teklif_tarihi}
              musteri={record.musteri}
            />
          );
        },
      },
    },
    {
      name: "musteri",
      label: "Firma",
      options: {
        customBodyRender: (value) => (value ? String(value).split(" ")[0] : ""),
      },
    },
    {
      name: "son_teslim_tarihi",
      label: "Teslim",
      options: {
        filter: false,
        customBodyRender: (value) => (
          <DateChip value={value} empty="Girilmemiş" />
        ),
      },
    },
    { name: "temsilci", label: "Temsilci" },
    {
      name: "teklif_no",
      label: "Ek / Not",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, meta) => {
          const record = teslim[meta.currentTableData[meta.rowIndex].index];
          if (!value || !record) return null;
          return (
            <ModalboxEkler
              key={`teslim-ek-${record.teklif_no}`}
              teklifNo={record.teklif_no}
              dosyaVar={record.dosya_var ? 1 : 0}
              onSaved={() => window.location.reload()}
            />
          );
        },
      },
    },
  ];

  const gecerColumns = [
    {
      name: "teklif_no",
      label: "Teklif No",
      options: {
        filter: false,
        customBodyRender: (value, meta) => {
          const record = gecerlilik[meta.currentTableData[meta.rowIndex].index];
          if (!value || !record) return value;
          return (
            <MalzemeListesiOnizleme
              teklifNo={value}
              teklifDili={record.teklif_dili}
              teklifTarihi={record.teklif_tarihi}
              musteri={record.musteri}
            />
          );
        },
      },
    },
    {
      name: "musteri",
      label: "Firma",
      options: {
        customBodyRender: (value) => (value ? String(value).split(" ")[0] : ""),
      },
    },
    {
      name: "gecerlilik_tarihi",
      label: "Geçerlilik",
      options: {
        filter: false,
        customBodyRender: (value) => <DateChip value={value} />,
      },
    },
    { name: "temsilci", label: "Temsilci" },
    {
      name: "teklif_no",
      label: "Ek / Not",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, meta) => {
          const record = gecerlilik[meta.currentTableData[meta.rowIndex].index];
          if (!value || !record) return null;
          return (
            <ModalboxEkler
              key={`gecer-ek-${record.teklif_no}`}
              teklifNo={record.teklif_no}
              dosyaVar={record.dosya_var ? 1 : 0}
              onSaved={() => window.location.reload()}
            />
          );
        },
      },
    },
    {
      name: "hatirlatma",
      label: "Hatırlatma",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, meta) => {
          const record = gecerlilik[meta.currentTableData[meta.rowIndex].index];
          if (!record) return null;
          const n = Number(value ?? record.hatirlatma ?? 0) || 0;
          const label = n > 0 ? `Hatırlat (${n})` : "Hatırlat";
          return (
            <button
              type="button"
              className="pano__btn pano__btn--warn"
              onClick={() => hatirlat(record.teklif_no)}
            >
              {label}
            </button>
          );
        },
      },
    },
    {
      name: "id",
      label: "İptal",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_v, meta) => {
          const record = gecerlilik[meta.currentTableData[meta.rowIndex].index];
          if (!record) return null;
          return (
            <button
              type="button"
              className="pano__btn pano__btn--danger"
              onClick={() => openIptal(record.teklif_no)}
            >
              İptal Maili
            </button>
          );
        },
      },
    },
    {
      name: "otomatik_eposta",
      label: "Oto Hat.",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, meta) => {
          const record = gecerlilik[meta.currentTableData[meta.rowIndex].index];
          if (!record) return null;
          return (
            <Checkbox
              size="small"
              checked={!!value}
              onChange={(e) => toggleOtoMail(record.teklif_no, e.target.checked)}
              color="primary"
              title="Otomatik hatırlatma maili"
            />
          );
        },
      },
    },
    {
      name: "otomatik_iptal",
      label: "Oto İptal",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, meta) => {
          const record = gecerlilik[meta.currentTableData[meta.rowIndex].index];
          if (!record) return null;
          return (
            <Checkbox
              size="small"
              checked={!!(value ?? record.otomatik_iptal)}
              onChange={(e) => toggleOtoIptal(record.teklif_no, e.target.checked)}
              color="primary"
              title="Otomatik iptal"
            />
          );
        },
      },
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <ToastContainer position="bottom-right" autoClose={4000} newestOnTop />
      <div className="pano">
        <div className="pano__header">
          <div>
            <h1 className="pano__title">Pano</h1>
            <p className="pano__subtitle">Teslim ve geçerlilik takibi</p>
          </div>
          <div className="pano__stats">
            <div className="pano__stat">
              Teslim yaklaşan <strong>{teslim.length}</strong>
            </div>
            <div className="pano__stat">
              Geçerlilik yaklaşan <strong>{gecerlilik.length}</strong>
            </div>
          </div>
        </div>

        <div className="pano__grid">
          <section className="pano__card">
            <div className="pano__card-head">
              <h2 className="pano__card-title">Teslim Süresi Yaklaşan Teklifler</h2>
              <span className="pano__badge">{teslim.length}</span>
            </div>
            <div className="pano__card-body">
              <MUIDataTable
                title=""
                data={teslim}
                columns={enhanceColumns(teslimColumns)}
                options={withTableOptions(commonOptions)}
              />
            </div>
          </section>

          <section className="pano__card">
            <div className="pano__card-head">
              <h2 className="pano__card-title">Teklif Geçerlilik Süresi Yaklaşanlar</h2>
              <span className="pano__badge">{gecerlilik.length}</span>
            </div>
            <div className="pano__card-body">
              <MUIDataTable
                title=""
                data={gecerlilik}
                columns={enhanceColumns(gecerColumns)}
                options={withTableOptions(commonOptions)}
              />
            </div>
          </section>
        </div>
      </div>

      <Dialog open={iptalOpen} onClose={() => setIptalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>İptal Maili — {iptalTeklifNo}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="İptal / red notu"
            fullWidth
            multiline
            minRows={3}
            value={redNotlari}
            onChange={(e) => setRedNotlari(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIptalOpen(false)}>Vazgeç</Button>
          <Button
            style={{ background: "#fc544b", color: "#fff" }}
            variant="contained"
            onClick={submitIptal}
          >
            İptal Et &amp; Mail At
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
